/**
 * MONSTER TOWN — GPU-PRESSURE GOVERNOR. OWNED BY PERF (src/perf/**).
 *
 * THE HOLE THIS FILLS. `GameRenderer.autoTune` sheds render scale when CPU WORK
 * runs long, and that is right as far as it goes — gating on the frame interval
 * alone is a trap, because an iPad in low-power mode (and every headless
 * browser) clamps requestAnimationFrame to 30 Hz and a game doing 3 ms of work
 * looks identical to one doing 30. But this game is not CPU-bound on an older
 * iPad; it is FILL-bound. A kid fills the plot, the shadow pass and the ink
 * shells eat the GPU, `workMs` stays at 4 ms because submitting draw calls is
 * cheap, and the renderer's one lever never moves. The adaptive scale that the
 * whole older-iPad story rests on was watching the wrong half of the frame.
 *
 * WHAT IS SAFE TO INFER FROM THE INTERVAL. Two signals, both immune to cadence
 * clamping:
 *
 *   1. ABSOLUTE. A sustained median interval above `HARD_SLOW_MS` (40 ms) cannot
 *      be produced by clamping. 60 Hz clamps to 16.7, 30 Hz clamps to 33.3; the
 *      only way to sit above 40 is to actually miss frames. 6.7 ms of headroom
 *      over the 30 Hz clamp means low-power mode never trips this.
 *
 *   2. RELATIVE. Compare the median interval to the FASTEST cadence this session
 *      has ever sustained (a decayed minimum of the 10th percentile). If the
 *      display can do 16.7 and we are sitting at 24, we are missing frames even
 *      though 24 is nowhere near 40. The floor self-calibrates: shedding scale
 *      makes frames cheaper, the observed floor drops with them, and the test
 *      keeps working at the new operating point.
 *
 * WHY IT DOES NOT OWN THE SCALE. Two controllers both writing `renderScale`
 * would fight — the renderer creeps back up whenever `workMs < 7`, which is
 * always true when the GPU is the bottleneck, so it would undo every reduction
 * within four seconds. Instead the governor publishes a PRESSURE HINT and the
 * renderer blends it into the one signal its existing hysteresis already reads
 * (`setPressureHint`, additive). One controller, two inputs.
 *
 * ZERO ALLOCATION IN THE HOT LOOP: preallocated ring buffer, preallocated sort
 * scratch, percentiles recomputed once every `RECALC_FRAMES` frames.
 */

import type { GameContext } from '../app/context'
import { engine } from '../core/engine'

/** Frames of interval history the percentiles are taken over (~4 s at 30 Hz). */
const WINDOW = 120
/** Recompute percentiles this often. Sorting 120 floats twice a second is free. */
const RECALC_FRAMES = 30
/**
 * A sustained median above this is missed frames, not a clamped display.
 * 30 Hz clamping lands at 33.3 ms, so this has 6.7 ms of headroom under it.
 */
const HARD_SLOW_MS = 40
/** Median this far above the session's best cadence means we are missing frames. */
const RELATIVE_SLOW = 1.35
/** Below this ratio the frame is landing on cadence — no pressure. */
const RELATIVE_OK = 1.12
/** Never believe a cadence floor faster than this (rAF noise on a fast desktop). */
const FLOOR_MIN_MS = 7
/**
 * A best-case cadence slower than this is not a display, it is a headless
 * browser on a software rasteriser — the screenshot and QA harnesses present at
 * 100-220 ms whatever the game does.
 *
 * This guard is not cosmetic. Without it the governor asserts pressure on EVERY
 * harness run, the renderer duly steps the render scale down to 0.6 and drops to
 * the low tier, and every screenshot the other crews take comes back
 * soft-edged with no shadows and no ink outlines — a perf change silently
 * rewriting the art review. No real iPad, in low power mode, throttled and on
 * fire, has a best-case frame of 60 ms; a device that did could not be rescued
 * by render scale anyway.
 */
const FLOOR_UNREAL_MS = 60
/** The floor decays back up slowly so one lucky fast frame cannot pin it forever. */
const FLOOR_DECAY = 1.0008
/**
 * Reported as "our work took this long" when the GPU is behind. It must exceed
 * the renderer's SLOW_WORK_MS (14) so the existing 30-frame hysteresis fires,
 * and it is deliberately not larger — the hint is a boolean dressed as a number.
 */
const PRESSURE_MS = 16

export interface GovernorReport {
  enabled: boolean
  /** Median frame interval over the window. */
  p50: number
  /** 10th percentile — the best cadence the window saw. */
  p10: number
  /** Decayed session-best cadence the median is judged against. */
  floorMs: number
  /** What the governor is currently telling the renderer. 0 = healthy. */
  pressureMs: number
  /** How many times pressure has been asserted since boot. */
  events: number
  frames: number
}

const ring = new Float64Array(WINDOW)
const scratch = new Float64Array(WINDOW)
let head = 0
let filled = 0
let sinceRecalc = 0

let p50 = 0
let p10 = 0
let floorMs = 0
let pressureMs = 0
let events = 0
let enabled = true
let totalFrames = 0

/** Insertion sort on the preallocated scratch — no allocation, N is 120. */
function percentiles(): void {
  const n = filled
  if (n < 8) return
  scratch.set(ring.subarray(0, n))
  for (let i = 1; i < n; i++) {
    const v = scratch[i] as number
    let j = i - 1
    while (j >= 0 && (scratch[j] as number) > v) {
      scratch[j + 1] = scratch[j] as number
      j--
    }
    scratch[j + 1] = v
  }
  p50 = scratch[Math.floor(n * 0.5)] as number
  p10 = scratch[Math.floor(n * 0.1)] as number
}

function update(): void {
  percentiles()
  if (p10 <= 0) return
  if (p10 > FLOOR_UNREAL_MS) {
    // Not a display we can reason about. Say nothing rather than something wrong.
    floorMs = p10
    pressureMs = 0
    return
  }

  // Session-best cadence, decaying gently upward so the floor can follow a
  // display that legitimately changed (low-power mode kicking in mid-session).
  const candidate = Math.max(FLOOR_MIN_MS, p10)
  floorMs = floorMs === 0 ? candidate : Math.min(candidate, floorMs * FLOOR_DECAY)

  const slow = p50 > HARD_SLOW_MS || p50 > floorMs * RELATIVE_SLOW
  const ok = p50 <= Math.max(HARD_SLOW_MS * 0.8, floorMs * RELATIVE_OK)

  if (slow && pressureMs === 0) {
    pressureMs = PRESSURE_MS
    events++
  } else if (ok && pressureMs !== 0) {
    pressureMs = 0
  }
}

export function installGovernor(ctx: GameContext): void {
  engine.addSystem({
    id: 'perf.governor',
    // Band 0-9 is input/camera; 90-99 is hud. This must run AFTER the frame it
    // is judging, so it sits at the very end of the render pass.
    order: 99,
    render: (_alpha, frameMs) => {
      totalFrames++
      // A frame gap that big is a tab switch or a scene build, not a slow GPU;
      // letting it into the window would spike the median for four seconds.
      if (frameMs > 0 && frameMs < 250) {
        ring[head] = frameMs
        head = (head + 1) % WINDOW
        if (filled < WINDOW) filled++
      }
      sinceRecalc++
      if (sinceRecalc < RECALC_FRAMES) return
      sinceRecalc = 0
      if (!enabled) {
        pressureMs = 0
        ctx.renderer.setPressureHint(0)
        return
      }
      update()
      ctx.renderer.setPressureHint(pressureMs)
    },
  })
}

export function setGovernorEnabled(on: boolean): void {
  enabled = on
  if (!on) pressureMs = 0
}

/** Forget the window — call after a scene build so the spike is not judged. */
export function resetGovernor(): void {
  head = 0
  filled = 0
  sinceRecalc = 0
  p50 = 0
  p10 = 0
  floorMs = 0
  pressureMs = 0
}

export function governorReport(): GovernorReport {
  return { enabled, p50, p10, floorMs, pressureMs, events, frames: totalFrames }
}
